import { isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { Icon } from '../../../../shared/icon/Icon';
import { Delete, Tick } from '../../../../shared/icons';
import { CustomTooltip } from '../../../../shared/tooltip';
import styles from './TableInstanceFilters.module.scss';

interface SharedNumberFilterProps {
    updateFilter: (value: number | undefined) => void;
    filter: number | undefined;
    testId: string;
    placeholder?: string;
}

export const SharedNumberFilter: React.FC<SharedNumberFilterProps> = ({ updateFilter, filter, testId, placeholder = 'Add filter...' }) => {
    const [includeDecimal, setIncludeDecimal] = useState<boolean>(false);
    const [filterNumberValue, setFilterNumberValue] = useState<number | undefined>(filter);

    const formattedNumberValue = useMemo(() => {
        if (isUndefined(filterNumberValue)) {
            return '';
        }
        let pattern = /(?=(?!^)\d{3}(?:\b|(?:\d{3})+)\b)/g;
        if (filterNumberValue.toString().includes('.')) {
            pattern = /(?=(?!^)\d{3}(?:\b|(?:\d{3})+)\b\.)/g;
        }
        return `${filterNumberValue.toString().replace(pattern, ',')}${includeDecimal ? '.' : ''}`;
    }, [includeDecimal, filterNumberValue]);

    const updateNumberValue = useCallback((stringValue: string) => {
        setIncludeDecimal(stringValue.length > 0 && stringValue[stringValue.length - 1] === '.');
        const numberValue = parseFloat(stringValue.replace(/,/g, ''));
        if (!isNaN(numberValue) || (isNaN(numberValue) && stringValue.length === 0)) {
            const value = !isNaN(numberValue) ? numberValue : undefined;
            setFilterNumberValue(value);
        }
    }, []);

    const updateNumberFilterKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => e.key.toLowerCase() === 'enter' && updateFilter(filterNumberValue), [updateFilter, filterNumberValue]);
    const updateNumberFilterClick = useCallback(() => updateFilter(filterNumberValue), [updateFilter, filterNumberValue]);
    const clearNumberFilter = useCallback(() => {
        setFilterNumberValue(undefined);
        updateFilter(undefined);
    }, [updateFilter]);

    return (
        <div className={styles.comparedFilterWrapper}>
            <input
                placeholder={placeholder}
                className={styles.tableFilterInput}
                data-testid={`${testId}-input`}
                onChange={e => updateNumberValue(e.target.value)}
                onKeyDown={updateNumberFilterKeyDown}
                value={formattedNumberValue}
                autoFocus
            />
            <CustomTooltip overlayText='Apply Filter'>
                <button onClick={updateNumberFilterClick} className={styles.setFilterButton}><Icon icon={Tick} fontSize={18} /></button>
            </CustomTooltip>
            {!isUndefined(filterNumberValue) &&
                <CustomTooltip overlayText='Clear Filter'>
                    <button onClick={clearNumberFilter} className={styles.clearFilterButton}><Icon icon={Delete} fontSize={18} /></button>
                </CustomTooltip>
            }
        </div>
    );
};
